type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferred: BIPEvent | null = null;

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferred = e as BIPEvent;
});

window.addEventListener('appinstalled', () => {
  deferred = null;
});

export function isStandalone(): boolean {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || (navigator as unknown as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

export function isIOS(): boolean {
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isAndroid(): boolean {
  return /Android/i.test(navigator.userAgent || '');
}

export function canPromptInstall(): boolean {
  return !!deferred && !isStandalone();
}

export type InstallOutcome =
  | { kind: 'system' }          // native install dialog shown
  | { kind: 'ios' }             // show: Share -> Home Screen
  | { kind: 'android' }         // show: browser menu -> Install
  | { kind: 'desktop' }         // show: browser menu -> Install
  | { kind: 'already' };

/**
 * One action, no redirects.
 * Android/desktop with prompt -> native dialog.
 * Otherwise returns which one-line hint to show.
 */
export async function requestInstall(): Promise<InstallOutcome> {
  if (isStandalone()) return { kind: 'already' };

  if (deferred) {
    const evt = deferred;
    deferred = null;
    try {
      await evt.prompt();
      await evt.userChoice;
    } catch {
      /* ignore */
    }
    return { kind: 'system' };
  }

  if (isIOS()) return { kind: 'ios' };
  if (isAndroid()) return { kind: 'android' };
  return { kind: 'desktop' };
}
